import { ChangeDetectorRef, EventEmitter, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IColumnDefinition } from '../types';
import { AlertService } from '@omni/services/alert/alert.service';
import { EXCLUDED_CONTACT_GLANCE_CARD_ATTRIBUTES } from '@omni/services/contact/contact.service';
import { debounceTime, firstValueFrom, Subject, takeUntil } from 'rxjs';
import {
  ControlDataType,
  DynamicForm,
} from '@omni/classes/dynamic-form/dynamic-form.class';
import { DEFAULT_FORM_LANGUAGE_CODE } from '@omni/components/shared/ind-display-form/ind-display-form.constants';
import { DynamicFormsService } from '@omni/services/dynamic-forms/dynamic-forms-service';
import { DeviceService } from '@omni/services/device/device.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { LocalizationService } from '@omni/services/localization/localization.service';
import { ConfigureColumnsPanelComponent } from '../panels/configure-columns.component';
import Fuse from 'fuse.js';
import { differenceBy, uniqBy } from 'lodash-es';
import { _ } from '@omni/utility/lodash-chain';

function tick(ms?: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

export abstract class BaseTableComponent {
  private _allColumns: IColumnDefinition[] = [];
  public columns: IColumnDefinition[] = [];
  public data: any[] = [];
  public loadingMore = false;
  public hasMoreData = true;
  public selectedRows = new Set<string>();
  public highlightedRows = new Set<string>();
  public sorted: { field: string; asc: boolean } | null = null;
  private readonly _pageSize = 50;
  public selectAll: boolean | null = false;
  // Cached Fuse index for the default (non-contact) table search, so repeated
  // keystrokes over the same item-set don't rebuild the index.
  private _cachedFuseItems: any[] | null = null;
  private _cachedFuseIndex: Fuse<any, any> | null = null;

  abstract get searchText(): string;
  abstract set searchText(value: string);
  abstract get selectionChanged(): EventEmitter<Set<string>>;

  protected readonly destroy$: Subject<boolean> = new Subject<boolean>();
  private readonly _syncCompleted$ = new Subject<void>();
  private readonly _loaded$ = new Subject<void>();
  private readonly _loaded: Promise<void> = firstValueFrom(this._loaded$);
  private _tableConfig: any = null;

  protected abstract get tableConfigKey(): string;
  protected abstract get searchKeys(): string[];

  constructor(
    protected readonly disk: DiskService,
    protected readonly alertService: AlertService,
    protected readonly dynamicFormService: DynamicFormsService,
    protected readonly translate: TranslateService,
    public readonly device: DeviceService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly localizationService: LocalizationService,
  ) {
    this._syncCompleted$
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(async () => {
        this.init();
      });
    this._syncCompleted$.next();
  }

  async onChanges(changes: SimpleChanges) {
    if (changes.searchText) {
      this.searchText = changes.searchText.currentValue;
      await this._loaded;
      this.loadData(true, this.sorted);
    }
  }

  onDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onSyncCompleted() {
    this._syncCompleted$.next();
  }

  private async getConfig() {
    const config = await this.disk.retrieve(this.tableConfigKey);
    return config?.raw;
  }

  private async saveConfig(config: any) {
    await this.disk.updateOrInsert(this.tableConfigKey, () => ({
      raw: config,
    }));
  }

  private async init() {
    const config = await this.getConfig();
    this._tableConfig = config;
    let savedColumns = config?.columns || [];
    const columns = await this.getColumns(savedColumns);
    this.updateColumns(columns);
    this.data = [];
    this.sorted = config?.sorted;
    this.hasMoreData = true;
    this.cdr.detectChanges();
    await tick();
    await this.onInitData();
    this._loaded$.next();
  }

  private updateColumns(columns: IColumnDefinition[]) {
    this._allColumns = [...columns];
    this.columns = columns.filter((column) => !column.hidden);
    this.cdr.markForCheck();
  }

  private async saveColumns() {
    this._tableConfig ??= {};
    this._tableConfig.columns = this._allColumns;
    this._tableConfig.sorted = this.sorted;
    await this.saveConfig(this._tableConfig);
  }

  abstract getFormDefinition(): { form: DynamicForm; langCode: string };

  private getColumnsFromMetadata(): IColumnDefinition[] {
    let { form: contactForm, langCode } = this.getFormDefinition();
    return _(contactForm.metadata)
      .flatMap((tab) => tab.controls)
      .filter(
        (control) =>
          !this.dynamicFormService.checkIfControlShouldNotBeVisible(control) &&
          !control.forceHide &&
          control.dataType != ControlDataType.StateType &&
          control.dataType != ControlDataType.StatusType &&
          control.dataType &&
          control.isVisible
      )
      .map((control) => {
        let displayTitle: string = '';
        const dt = control.displayNames.find(
          (value) => value.languagecode == langCode,
        );
        if (dt) {
          displayTitle =
            dt.languagecode == DEFAULT_FORM_LANGUAGE_CODE
              ? this.translate.instant(dt.description)
              : dt.description;
        } else {
          let en = control.displayNames.find(
            (value) => value.languagecode == '1033',
          ); // default to english if no translation for the specific language;
          displayTitle = en
            ? en.description
            : (control.displayNames.find(
                (value) => value.languagecode == '0000',
              )?.description ?? '');
        }
        let field = control.attributeName;
        if (control.dataType == ControlDataType.LookupType) {
          field = `_${field}_value_Formatted`;
        } else if (control.dataType !== ControlDataType.StringType) {
          field = `${field}_Formatted`;
        }
        return {
          name: displayTitle,
          field,
          width: 200,
          sortable: true,
        };
      })
      .value();
  }

  protected abstract getDefaultColumns(): IColumnDefinition[];

  private async getColumns(savedColumns: IColumnDefinition[]): Promise<IColumnDefinition[]> {
    const metadataColumns = uniqBy(
      [...this.getDefaultColumns(), ...this.getColumnsFromMetadata()],
      'field',
    );
    savedColumns = savedColumns.filter((c) =>
      metadataColumns.some((c2) => c2.field === c.field),
    );
    savedColumns.push(
      ...differenceBy(metadataColumns, savedColumns, 'field'),
    );
    savedColumns = savedColumns.map((col) => {
      const metadata = metadataColumns.find((c) => c.field === col.field);
      return {
        ... metadata || col,
        hidden: col.hidden ?? metadata?.hidden ?? false,
        width: col.width ?? metadata?.width ?? 200,
      };
    });
    return savedColumns;
  }

  protected abstract onInitData(): Promise<void>;

  protected abstract getItems(): any[] | Promise<any[]>;
  protected mapItem(item: any) {
    return item;
  }

  private _applySearch(
    items: any[],
    searchText: string,
    sort: { field: string; asc: boolean },
  ) {
    if (searchText?.trim()) {
      items = this.searchItems(items, searchText);
    }
    if (sort?.field) {
      items.sort((a, b) => {
        const A = a[sort.field];
        const B = b[sort.field];
    
        // normalize to string for safety
        const valA = A == null ? '' : String(A);
        const valB = B == null ? '' : String(B);
    
        const compare = valA.localeCompare(valB, undefined, {
          numeric: true,
          sensitivity: 'base', // ignore case and accents
        });
    
        return sort.asc ? compare : -compare;
      });
    }

    return items;
  }

  /**
   * Filter + rank rows for `searchText` and attach an `__highlighted` map.
   * Default implementation runs a generic Fuse search across `searchKeys`.
   * Subclasses (e.g. contacts) override this to plug in domain-specific ranking
   * (shared name-search utility) while keeping the same highlight contract.
   */
  protected searchItems(items: any[], searchText: string): any[] {
    // Cache the Fuse index per item-set so repeated keystrokes don't rebuild it.
    if (this._cachedFuseItems !== items) {
      this._cachedFuseIndex = new Fuse(items, {
        keys: this.searchKeys || [this.columns?.[0]?.field || 'name'],
        threshold: 0.6,
        includeMatches: true,
      });
      this._cachedFuseItems = items;
    }
    const fuse = this._cachedFuseIndex;

    return fuse.search(searchText).map((result) => {
      const highlighted: Record<string, string> = {};

      for (const match of result.matches || []) {
        const value = match.value;
        if (!value) continue;

        let highlightedText = '';
        let lastIndex = 0;

        for (const [start, end] of match.indices) {
          // Add non-matched portion
          highlightedText += value.slice(lastIndex, start);
          // Wrap match with <b>
          highlightedText += `<b>${value.slice(start, end + 1)}</b>`;
          lastIndex = end + 1;
        }
        // Append remaining part
        highlightedText += value.slice(lastIndex);

        highlighted[match.key] = highlightedText;
      }

      const highlightedRow = Object.create(result.item);
      highlightedRow.__highlighted = highlighted;
      return highlightedRow;
    });
  }

  private async _loadData(
    clear: boolean = false,
    sort?: { field: string; asc: boolean },
  ) {
    if (clear) {
      this.data = [];
      this._cachedFuseItems = null;
      this._cachedFuseIndex = null;
    }
    const lastIndex = this.data.length;
    let newData = await Promise.resolve(this.getItems());
    newData = this._applySearch(newData, this.searchText, sort);
    const totalCount = newData.length;
    newData = newData.slice(lastIndex, lastIndex + this._pageSize);
    newData = await Promise.all(
      newData.map(async (item) => Promise.resolve(this.mapItem(item))),
    );
    this.sorted = sort;
    this.data = [...this.data, ...newData];
    this.loadingMore = false;
    this.hasMoreData = totalCount > this.data.length;
    if (newData.length > 0 && this.selectAll) {
      this.selectAll = null; // reset select all if new data loaded
    }
    this.cdr.markForCheck();
  }

  async onLoadMore() {
    await this._loaded;
    await this.loadData(false, this.sorted);
  }

  protected async refreshData() {
    await this._loaded;
    await this.loadData(true, this.sorted);
  }

  private async loadData(clear: boolean, sort?: { field: string; asc: boolean }) {
    this.loadingMore = true;
    this.cdr.detectChanges();
    await tick();
    await this._loaded;
    await this._loadData(clear, sort);
    this.cdr.markForCheck();
  }

  onRowSelected(event: { row: any; checked: boolean }) {
    if (event.checked) {
      this.selectedRows.add(event.row.id);
    } else {
      this.selectedRows.delete(event.row.id);
    }
    let selectAll: boolean | null = null; // null means indeterminate
    if (this.selectedRows.size === this.data.length) {
      selectAll = true;
    } else if (this.selectedRows.size === 0) {
      selectAll = false;
    }
    this.selectAll = selectAll;
    this.cdr.markForCheck();
    this.selectionChanged.emit(this.selectedRows);
  }

  onSelectAll(checked: boolean) {
    if (checked) {
      this.data.forEach((item) => this.selectedRows.add(item.id));
    } else {
      this.selectedRows.clear();
    }
    this.selectAll = checked;
    this.cdr.markForCheck();
    this.selectionChanged.emit(this.selectedRows);
  }

  async onSort(event: { column: IColumnDefinition; asc: boolean }) {
    await this.loadData(
      true,
      event ? { field: event.column.field, asc: event.asc } : undefined,
    );
    this.saveColumns();
  }

  private async configureColumns() {
    const { data, role } = await this.alertService.showPanel({
      title: 'Edit Columns',
      content: {
        component: ConfigureColumnsPanelComponent,
        inputs: { columns: this._allColumns },
      },
      buttons: [
        { key: 'cancel', label: 'Cancel' },
        { key: 'ok', label: 'Apply', primary: true },
      ],
    });
    if (data && role === 'ok') {
      this.updateColumns(data);
      await this.saveColumns();
    }
  }

  async onColumnsModified(columns: IColumnDefinition[]) {
    const reorderedMap = new Map(columns.map((c) => [c.field, c]));

    let visibleIndex = 0;

    this.updateColumns(
      this._allColumns.map((col) => {
        if (!col.hidden && reorderedMap.has(col.field)) {
          const reorderedCol = columns[visibleIndex];
          visibleIndex++;
          return reorderedCol;
        }
        return col; // keep hidden column as-is
      }),
    );
    await this.saveColumns();
  }

  handleHeaderAction(action: string) {
    switch (action) {
      case 'columns':
        this.configureColumns();
        break;
      case 'filter':
        this.configureFilter();
        break;
      case 'tags':
        this.configureTags();
        break;
    }
  }

  /** Subclasses opt in to native HTML5 row-drag by overriding this. */
  rowDraggable = false;

  /** Default no-op. Subclasses (e.g. ContactsTableComponent on the activities
   *  page) override to populate event.dataTransfer with the drag payload. */
  onRowDragStart(_event: { row: any; event: DragEvent }): void {
    // intentional no-op — subclasses override
  }

  abstract onRowClicked(row: any): void;
  abstract onRowAction(row: any, action: 'new-activity' | 'more'): void;
  protected abstract configureFilter(): void;
  protected abstract configureTags(): void;

  trackById(index: number, item: any) { return item?.id || item?.ID || index; }
}
